<template>
  <div class="ex-checkpoint-header">
    <div class="ex-checkpoint-crumb">
      <Breadcrumbs
        :breadcrumbsList="props.breadcrumbs"
        @onClickBreadcrumb="handleBreadcrumbClick"
      />
      <div class="ex-right-side-checkpoint">
        <div 
          class="hide-left-border center-vertical ex-chatter-type-icon"
          @click="onChatterClick"
        >
          <v-img
            class="chatter-icon"
            :src="require('@components/assets/ChatterIcon.svg')"
          />
        </div>
        <Button
          v-if="checkpointState === 'Editable'"
          size="small"
          :outline="'outline-hover'"
          label="Edit"
          class="ex-edit-checkpoint"
          @onClick="onEditClick"
        />
      </div>
    </div>
    <CheckpointLevelBand
      v-if="checkpointState !== 'Edit'"
      class="ex-level-band"
      :level="readiness_level.name"
    />
    <div v-if="checkpointState === 'Edit'" class="ex-checkpoint-picker">
      <CheckpointLevelPicker
        label="Apprentice Level"
        :levels="levelItems"
        :value="selectedLevel"
        @item-selected="resetLevel"
        :class="['add-checkpoint-level-picker', { 'add-checkpoint-level-picker-dirty': isLevelDirty }]"
      />
      <div class="ex-right-buttons">
        <Button
          size="small"
          :outline="'outline-hover'"
          label="Discard"
          class="ex-discard-checkpoint"
          @onClick="onDiscardClick"
        />
        <Button
          size="small"
          :fullwidth="isSaveDisabled ? 'disable' : 'hover'"
          :label="'Save'"
          class="ex-save-checkpoint"
          @onClick="onSaveClick"
          :loading="isSaving ? 'left' : null"
        />
      </div>
    </div>
    <div v-if="checkpointState === 'Edit' && isStorybook" class="ex-checkpoint-criteria">
      <CriteriaInput
        class="check-criteria-input mb-10"
        :criteria="criteriaInput"
        :criteriaPrefix="criteriaPrefix"
        :dropdownItems="dropdownItems"
        :isInputDirty="isInputDirty"
        :isPrefixDirty="isPrefixDirty"
        @update:criteria="criteriaInput = $event"
        @update:criteriaPrefix="criteriaPrefix = $event"
      />
    </div>
    <div v-if="checkpointState === 'Edit' && !isStorybook" class="ex-checkpoint-criteria checkpoint">
      <div class="">
        <ExTextInput
          label="Criteria*"
          placeholder="Add criteria"
          :value="criteriaInput"
          required
          dropdownSide="left"
          :dropdownItems="dropdownItems"
          :class="['add-criteria', { 'input-dirty': isInputDirty, 'prefix-dirty': isPrefixDirty }]"
          :showCustomIcon="true"
          @dropdown-input="handlePrefixChange"
          :selectedItem="criteriaPrefix"
          @input="handleCriteriaInput"
        ></ExTextInput>
      </div>
    </div>
    <div
      v-if="checkpointState === 'Read Only'"
      class="ex-checkpoint-header-criteria"
    >
      {{ criteriaPrefix }} {{ criteria }}
    </div>
    <div
      v-if="checkpointState === 'Editable'"
      class="ex-checkpoint-header-criteria-editable"
    >
      <span class="ex-checkpoint-header-criteria-editable-label"
        >Inspection Criteria</span
      >
      <span class="ex-checkpoint-header-criteria-editable-criteria">
        <span class="prefix">{{ criteriaPrefix }}</span> {{ criteria }}</span
      >
    </div>
    <!-- <div v-if="checkpointState === 'Editable'" class="ex-checkpoint-header-instructions">
      {{ instructions }}
    </div> -->
  </div>
</template>

<script setup>
import { defineProps, computed, ref, watch } from "vue";
import Breadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import CheckpointLevelBand from "@components/CheckpointLevelBand/CheckpointLevelBand.vue";
import Button from "@components/buttons/Button.vue";
import CheckpointLevelPicker from "@components/CheckpointLevelPicker/CheckpointLevelPicker.vue";
import CriteriaInput from "@components/CriteriaInput/CriteriaInput.vue";
import ExTextInput from "@components/TextInput/ExTextInput.vue";
import { changeCheckpointV2 } from "@/api";

// Loading state for save button
const isSaving = ref(false);

const props = defineProps({
  breadcrumbs: {
    type: Array,
    default: () => [
      {
        text: "Checklist",
        href: "",
      },
      {
        text: "Checkpoint",
        href: "",
        disabled: true,
      },
    ],
  },
  state: {
    type: String,
    validator: (value) => ["Read Only", "Editable", "Edit"].includes(value),
    default: "Read Only",
  },
  readiness_level: {
    type: Object,
    default: () => ({
      id: 1,
      name: "Apprentice",
      value: "apprentice",
    }),
  },
  criteria: {
    type: String,
    default: "",
  },
  instructions: {
    type: String,
    default: "",
  },
  checkpointPrefix: {
    type: String,
    default: "Can",
  },
  isStorybook: {
    type: Boolean,
    default: false,
  },
  // Props needed for API call
  checkpointId: {
    type: [Number, String],
    default: null,
  },
  responsibilityId: {
    type: [Number, String],
    default: null,
  },
  checkpointLevel: {
    type: [Number, String],
    default: 1,
  },
  checkpointStatus: {
    type: String,
    default: 'active',
  },
  checkpointInstructions: {
    type: String,
    default: '',
  },
});
const emit = defineEmits([
  "input",
  "reset-level",
  "onSave",
  "update:state",
  "breadcrumb-click",
  "update:criteriaInput",
  "update:criteriaPrefix",
  "checkpoint-updated",
  "open-chatter",
]);

const criteriaInput = ref(props.criteria);
const criteriaPrefix = ref(props.checkpointPrefix || "Can");
const dropdownItems = ref(["Can", "Does", "Demonstrated", "Proved"]);
const levelItems = ref([
  { id: 1, name: "Apprentice", value: "apprentice" },
  { id: 2, name: "Professional", value: "professional" },
  { id: 3, name: "Coach", value: "coach" },
  { id: 4, name: "Master", value: "master" },
]);
const checkpointState = ref(props.state);

// Store initial values to track "dirty" state
const initialCriteria = ref(props.criteria);
const initialCriteriaPrefix = ref(props.checkpointPrefix || "Can");
const initialLevel = ref(props.readiness_level);
const selectedLevel = ref(props.readiness_level);

watch(
  () => props.state,
  (newState) => {
    emit("update:state", newState);
    checkpointState.value = newState;
    // Capture initial values from PROPS when entering Edit mode
    if (newState === "Edit") {
      initialCriteria.value = props.criteria;
      initialCriteriaPrefix.value = props.checkpointPrefix || "Can";
      initialLevel.value = props.readiness_level;
      // Sync local refs with props to ensure clean state
      criteriaInput.value = props.criteria;
      criteriaPrefix.value = props.checkpointPrefix || "Can";
      selectedLevel.value = props.readiness_level;
    }
  }
);

watch(
  () => props.checkpointPrefix,
  (newPrefix) => {
    if (newPrefix) {
      criteriaPrefix.value = newPrefix;
      // Don't update initial value during Edit mode - we want to track changes!
    }
  }
);

watch(
  () => props.criteria,
  (newCriteria) => {
    criteriaInput.value = newCriteria;
    // Don't update initial value during Edit mode - we want to track changes!
  }
);

const resetLevel = (newLevel) => {
  selectedLevel.value = newLevel;
};

// 🔹 Computed property to check if input is dirty (for highlighting)
const isInputDirty = computed(() => {
  const criteriaIsDirty = criteriaInput.value !== initialCriteria.value;
  return criteriaIsDirty;
});

// 🔹 Computed property to check if prefix is dirty (for prefix highlighting)
const isPrefixDirty = computed(() => {
  const isDirty = criteriaPrefix.value !== initialCriteriaPrefix.value;
  return isDirty;
});

// 🔹 Computed property to check if level is dirty
const isLevelDirty = computed(() => {
  return selectedLevel.value?.id !== initialLevel.value?.id;
});

// 🔹 Computed property to disable Save button based on dirty state
const isSaveDisabled = computed(() => {
  const criteriaIsEmpty = !criteriaInput.value || criteriaInput.value.trim() === "";
  const prefixIsDirty = criteriaPrefix.value !== initialCriteriaPrefix.value;
  const criteriaIsDirty = criteriaInput.value !== initialCriteria.value;
  const levelIsDirty = isLevelDirty.value;
  
  // Disable if criteria is empty
  if (criteriaIsEmpty) {
    return true;
  }
  
  // Disable if nothing has changed (not dirty)
  if (!prefixIsDirty && !criteriaIsDirty && !levelIsDirty) {
    return true;
  }
  
  // Enable if something changed and criteria is not empty
  return false;
});

// 🔹 Handle criteria input changes
const handleCriteriaInput = (value) => {
  criteriaInput.value = value;
  emit("update:criteriaInput", value);
};

// 🔹 Handle prefix changes
const handlePrefixChange = (value) => {
  criteriaPrefix.value = value;
  emit("update:criteriaPrefix", value);
};

// 🔹 Switch to Edit mode
const onEditClick = () => {
  emit("update:state", "Edit");
  checkpointState.value = "Edit";
  // Store initial values from PROPS (source of truth) when entering Edit mode
  initialCriteria.value = props.criteria;
  initialCriteriaPrefix.value = props.checkpointPrefix || "Can";
  initialLevel.value = props.readiness_level;
  // Sync local refs with props to ensure clean state
  criteriaInput.value = props.criteria;
  criteriaPrefix.value = props.checkpointPrefix || "Can";
  selectedLevel.value = props.readiness_level;
};

// 🔹 Discard edits → revert all changes and go back to Editable
const onDiscardClick = () => {
  // Revert criteria, prefix, and level to their initial values
  criteriaInput.value = initialCriteria.value;
  criteriaPrefix.value = initialCriteriaPrefix.value;
  selectedLevel.value = initialLevel.value;
  
  // Emit updates to notify parent of reverted values
  emit("update:criteriaInput", initialCriteria.value);
  emit("update:criteriaPrefix", initialCriteriaPrefix.value);
  
  // Change state back to Editable
  emit("update:state", "Editable");
  checkpointState.value = "Editable";
};

// 🔹 Save → call API with loader, emit data and go back to Editable
const onSaveClick = async () => {
  // If checkpoint IDs are available, call the API
  if (props.checkpointId && props.responsibilityId) {
    isSaving.value = true;
    
    try {
      // Safely parse accountId from localStorage
      let accountId = null;
      try {
        const storedAccountId = localStorage.getItem("currentAccountID");
        if (storedAccountId) {
          accountId = JSON.parse(storedAccountId);
        }
      } catch (parseError) {
        console.error("Error parsing currentAccountID from localStorage:", parseError);
      }
      
      if (accountId) {
        const payload = {
          criteria: criteriaInput.value,
          prefix: criteriaPrefix.value.toLowerCase(),
          instructions: props.checkpointInstructions || "<p></p>",
          proficiency_level: selectedLevel.value?.id || props.checkpointLevel,
          status: props.checkpointStatus,
          account: accountId,
          responsibility: props.responsibilityId,
        };
        
        const response = await changeCheckpointV2(
          accountId,
          props.responsibilityId,
          props.checkpointId,
          payload
        );
        
        // Emit event with API response data to update parent (emit even if data is empty for consistent state)
        emit("checkpoint-updated", response?.data || null);
      }
    } catch (error) {
      console.error("Error updating checkpoint:", error);
    } finally {
      isSaving.value = false;
    }
  }
  
  // Emit the original onSave event for backwards compatibility
  emit("onSave", {
    criteria: criteriaInput.value,
    prefix: criteriaPrefix.value,
    level: selectedLevel.value,
  });
  emit("update:state", "Editable");
  checkpointState.value = "Editable";
};

// 🔹 Handle breadcrumb click
const handleBreadcrumbClick = (breadcrumb) => {
  emit("breadcrumb-click", breadcrumb);
};

// 🔹 Handle chatter icon click
const onChatterClick = () => {
  emit("open-chatter", {
    id: props.checkpointId,
    level: props.readiness_level?.name || 'Apprentice',
    criteria: props.criteria,
    prefix: props.checkpointPrefix,
    instructions: props.checkpointInstructions,
  });
};
</script>
<style scoped>
.checkpoint ::v-deep .v-text-field {
  padding-top: 0px !important;
}

.checkpoint ::v-deep .level-picker.v-text-field .v-input__control .v-input__slot .v-input__append-inner,
.checkpoint ::v-deep .level-picker.v-text-field .v-input__control .v-input__slot .v-input__prepend-inner {
  margin-top: 4px;
}
::v-deep .add-criteria .left-can-prepand {
  min-width: 65px;
  top: -10px;
  margin-top: 0 !important;
  padding-top: 0 !important;
}
::v-deep .add-criteria .left-demonstrated-prepand {
  min-width: 132px;
  top: -10px;
}
::v-deep .add-criteria .left-proved-prepand {
  min-width: 80px;
  top: -10px;
}
::v-deep .add-criteria.ex_text_input-demonstrated-prepand .v-text-field .v-input__control .v-input__slot label.v-label--active {
  margin-left: -60px !important;
}
::v-deep .add-criteria.ex_text_input-proved-prepand .v-text-field .v-input__control .v-input__slot label.v-label--active {
  margin-left: -10px !important;
}

/* Purple/lavender background when only criteria is dirty (text input area only, not prefix dropdown) */
/* For "Can" prefix (default ~65px + 6px gap) */
::v-deep .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-can-prepand .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden;
  background: linear-gradient(to right, white 0%, white 71px, #D9D6FE 71px, #D9D6FE 100%) !important;
  z-index: 0 !important;
}
::v-deep .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-can-prepand .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}

/* For "Demonstrated" prefix (~132px + 6px gap) */
::v-deep .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-demonstrated-prepand .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden;
  background: linear-gradient(to right, white 0%, white 143px, #D9D6FE 143px, #D9D6FE 100%) !important;
  z-index: 0 !important;
}
::v-deep .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-demonstrated-prepand .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}

/* For "Proved" prefix (~80px + 6px gap) */
::v-deep .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-proved-prepand .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden;
  background: linear-gradient(to right, white 0%, white 91px, #D9D6FE 91px, #D9D6FE 100%) !important;
  z-index: 0 !important;
}
::v-deep .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-proved-prepand .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}

/* Purple/lavender background when only prefix is dirty (only prefix dropdown, not text area) */
/* For "Can" prefix */
::v-deep .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden;
  background: linear-gradient(to right, #D9D6FE 0%, #D9D6FE 79px, white 65px, white 100%) !important;
  z-index: 0 !important;
}
::v-deep .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
::v-deep .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand .v-text-field .v-input__control .v-input__slot .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
::v-deep .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand .code-select-wrapper {
  background: transparent !important;
}
::v-deep .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand .level-picker .v-input__control .v-input__slot {
  background: transparent !important;
}
::v-deep .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand .level-picker .v-input__control fieldset {
  background: transparent !important;
}
::v-deep .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand .v-select__selections {
  background: transparent !important;
}

/* For "Demonstrated" prefix */
::v-deep .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden;
  background: linear-gradient(to right, #D9D6FE 0%, #D9D6FE 147px, white 137px, white 100%) !important;
  z-index: 0 !important;
}
::v-deep .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
::v-deep .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand .v-text-field .v-input__control .v-input__slot .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
::v-deep .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand .code-select-wrapper {
  background: transparent !important;
}
::v-deep .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand .level-picker .v-input__control .v-input__slot {
  background: transparent !important;
}
::v-deep .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand .level-picker .v-input__control fieldset {
  background: transparent !important;
}
::v-deep .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand .v-select__selections {
  background: transparent !important;
}

/* For "Proved" prefix */
::v-deep .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden;
  background: linear-gradient(to right, #D9D6FE 0%, #D9D6FE 95px, white 85px, white 100%) !important;
  z-index: 0 !important;
}
::v-deep .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
::v-deep .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand .v-text-field .v-input__control .v-input__slot .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
::v-deep .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand .code-select-wrapper {
  background: transparent !important;
}
::v-deep .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand .level-picker .v-input__control .v-input__slot {
  background: transparent !important;
}
::v-deep .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand .level-picker .v-input__control fieldset {
  background: transparent !important;
}
::v-deep .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand .v-select__selections {
  background: transparent !important;
}

/* Purple/lavender background when BOTH prefix AND criteria are dirty (entire input) */
::v-deep .add-criteria.prefix-dirty.input-dirty .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden;
  background-color: #D9D6FE !important;
  z-index: 0 !important;
}
::v-deep .add-criteria.prefix-dirty.input-dirty .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
::v-deep .add-criteria.prefix-dirty.input-dirty .v-text-field .v-input__control .v-input__slot .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
::v-deep .add-criteria.prefix-dirty.input-dirty .code-select-wrapper {
  background: transparent !important;
}
::v-deep .add-criteria.prefix-dirty.input-dirty .level-picker .v-input__control .v-input__slot {
  background: transparent !important;
}
::v-deep .add-criteria.prefix-dirty.input-dirty .level-picker .v-input__control fieldset {
  background: transparent !important;
}
::v-deep .add-criteria.prefix-dirty.input-dirty .v-select__selections {
  background: transparent !important;
}

::v-deep .add-criteria.prefix-dirty .v-text-field .v-input__control .v-input__slot input {
  background-color: transparent !important;
}

::v-deep .add-criteria.prefix-dirty .v-text-field .v-input__control .v-input__slot .v-input__prepend-inner {
  background-color: transparent !important;
}
::v-deep .add-checkpoint-level-picker-dirty .picker-trigger {
  border-radius: 8px !important;
  border: 4px solid #7F56D9 !important;
}
::v-deep .add-checkpoint-level-picker-dirty .picker-trigger.open {
  box-shadow: 0 0 0 3px #e9d7ff;
  border: 1px solid #d6bbfb !important;
}

</style>

<style>
.ex-checkpoint-header {
  display: flex;
  flex-direction: column;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  padding: 10px 15px 10px 15px;
}
.ex-checkpoint-header-criteria {
  font-family: Inter;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: #000000;
  padding: 12px 10px 4px 1px;
}
.ex-checkpoint-header-criteria-editable {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 6px;
}
.ex-checkpoint-header-criteria-editable-label {
  font-family: Inter;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: #000000;
}
.ex-checkpoint-header-criteria-editable-criteria {
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #666666;
}
.ex-checkpoint-header-criteria-editable-criteria .prefix {
  text-transform: capitalize;
}
.ex-checkpoint-crumb {
  padding: 6px 0px 12px 0px;
  display: flex;
  justify-content: space-between;
}
.ex-chatter-type-icon {
  display: grid;
  justify-content: end;
}
.ex-chatter-type-icon {
  padding: 6px;
  text-align: center;
  width: fit-content;
  cursor: url("../assets/cursorArrow.svg"), auto;
  position: relative;
  left: -10px;
}
.ex-chatter-type-icon:hover {
  background-color: #f2edff;
  padding: 6px;
  text-align: center;
  box-shadow: 0px 4px 4px 0px #0000000f;
  border-radius: 8px;
}
.ex-right-side-checkpoint {
  display: flex;
  position: relative;
}
.ex-edit-checkpoint {
  width: 55px;
}
.ex-discard-checkpoint {
  width: 80px;
}
.ex-save-checkpoint {
  width: 61px;
}
.ex-checkpoint-header-instructions {
  font-family: Inter;
  font-weight: 400;
  font-size: 13px;
  line-height: 24px;
  color: #475467;
  margin-left: 2px;
  margin-top: -5px;
}
.ex-checkpoint-picker {
  display: flex;
  position: relative;
  top: -10px;
}
.ex-checkpoint-picker .level-picker {
  margin-left: -5px;
}
.ex-checkpoint-criteria {
  margin-left: 8px;
  margin-top: -10px;
  margin-right: 8px;
}
.ex-checkpoint-criteria .check-criteria-input {
  margin-bottom: 0px !important;
}
.ex-right-buttons {
  display: flex;
  gap: 5px;
  position: relative;
  left: -12px;
  top: 4px;
}
</style>
